import { useCallback } from 'react';
import useAuthenticatedApi from '../../../Authentication/components/hooks/useAuthenticatedApi';
import {
    WorkflowDefinition,
    TestRunResult,
    WorkflowCatalog,
    WorkflowRunSummary,
    WorkflowRunDetail,
    WorkflowRunStatus,
    WorkflowTriggerType,
} from './types';

export interface SavedWorkflow {
    definition: WorkflowDefinition;
    enabled: boolean;
    exists: boolean;
    publishedVersionId: number | null;
    publishedVersionNumber: number | null;
    hasUnpublishedChanges: boolean;
}

export interface WorkflowVersionSummary {
    id: number;
    versionNumber: number;
    notes: string | null;
    createdAt: string;
    createdBy: number | null;
}

export interface WorkflowSummary {
    id: number;
    activationId: number;
    name: string | null;
    triggerType: WorkflowTriggerType;
    eventName: string | null;
    enabled: boolean;
    publishedVersionId: number | null;
    updatedAt: string;
}

/**
 * An activation has one qrScan workflow (managed through the legacy singular
 * routes when no workflowId is given) plus any number of event-triggered
 * workflows (always managed by id). Passing a workflowId routes every call to
 * the id-based endpoints.
 */
export default function useWorkflow() {
    const api = useAuthenticatedApi();

    const getWorkflow = useCallback(
        async (
            activationId: number,
            workflowId?: number | null,
        ): Promise<SavedWorkflow> => {
            const response = await api.get(
                workflowId
                    ? `/activations/${activationId}/workflows/${workflowId}`
                    : `/activations/${activationId}/workflow`,
            );
            const data = response.data;
            return {
                definition: data.definition ?? { nodes: [], edges: [] },
                enabled: !!data.enabled,
                exists: !!data.exists,
                publishedVersionId: data.publishedVersionId ?? null,
                publishedVersionNumber: data.publishedVersionNumber ?? null,
                hasUnpublishedChanges: !!data.hasUnpublishedChanges,
            };
        },
        [api],
    );

    const saveWorkflow = useCallback(
        async (
            activationId: number,
            definition: WorkflowDefinition,
            enabled: boolean,
            workflowId?: number | null,
        ): Promise<SavedWorkflow> => {
            const response = await api.put(
                workflowId
                    ? `/activations/${activationId}/workflows/${workflowId}`
                    : `/activations/${activationId}/workflow`,
                { definition, enabled },
            );
            const data = response.data;
            return {
                definition: data.definition ?? definition,
                enabled: !!data.enabled,
                exists: true,
                publishedVersionId: data.published_version_id ?? null,
                publishedVersionNumber: null,
                hasUnpublishedChanges: true,
            };
        },
        [api],
    );

    const listWorkflows = useCallback(
        async (activationId: number): Promise<WorkflowSummary[]> => {
            const response = await api.get(
                `/activations/${activationId}/workflows`,
            );
            return response.data.workflows ?? [];
        },
        [api],
    );

    const createWorkflow = useCallback(
        async (
            activationId: number,
            fields: {
                name: string;
                triggerType: WorkflowTriggerType;
                eventName?: string;
            },
        ): Promise<WorkflowSummary> => {
            const response = await api.post(
                `/activations/${activationId}/workflows`,
                fields,
            );
            const data = response.data;
            return {
                id: data.id,
                activationId: data.activation_id,
                name: data.name,
                triggerType: data.trigger_type,
                eventName: data.event_name,
                enabled: !!data.enabled,
                publishedVersionId: data.published_version_id ?? null,
                updatedAt: data.updated_at,
            };
        },
        [api],
    );

    const deleteWorkflow = useCallback(
        async (activationId: number, workflowId: number): Promise<void> => {
            await api.delete(
                `/activations/${activationId}/workflows/${workflowId}`,
            );
        },
        [api],
    );

    const importWorkflow = useCallback(
        async (
            activationId: number,
            payload: {
                name?: string | null;
                triggerType: WorkflowTriggerType;
                eventName?: string | null;
                enabled?: boolean;
                definition: WorkflowDefinition;
            },
        ): Promise<WorkflowSummary> => {
            const response = await api.post(
                `/activations/${activationId}/workflows/import`,
                payload,
            );
            const data = response.data;
            return {
                id: data.id,
                activationId: data.activationId ?? data.activation_id,
                name: data.name,
                triggerType: data.triggerType ?? data.trigger_type,
                eventName: data.eventName ?? data.event_name,
                enabled: !!data.enabled,
                publishedVersionId:
                    data.publishedVersionId ??
                    data.published_version_id ??
                    null,
                updatedAt: data.updatedAt ?? data.updated_at,
            };
        },
        [api],
    );

    const testRun = useCallback(
        async (
            activationId: number,
            definition: WorkflowDefinition,
            scannedInput: string,
        ): Promise<TestRunResult> => {
            try {
                const response = await api.post(
                    `/activations/${activationId}/workflow/test-run`,
                    { definition, scannedInput },
                );
                return response.data as TestRunResult;
            } catch (error: any) {
                return {
                    success: false,
                    error:
                        error.response?.data?.error ??
                        error.message ??
                        'Test run failed',
                    details: error.response?.data?.details,
                };
            }
        },
        [api],
    );

    const getCatalog = useCallback(async (): Promise<WorkflowCatalog> => {
        const response = await api.get(`/activations/workflow/catalog`);
        return response.data as WorkflowCatalog;
    }, [api]);

    const publishWorkflow = useCallback(
        async (
            activationId: number,
            notes?: string,
            workflowId?: number | null,
        ): Promise<{ publishedVersionNumber: number }> => {
            const response = await api.post(
                workflowId
                    ? `/activations/${activationId}/workflows/${workflowId}/publish`
                    : `/activations/${activationId}/workflow/publish`,
                { notes },
            );
            return {
                publishedVersionNumber: response.data.publishedVersionNumber,
            };
        },
        [api],
    );

    const listVersions = useCallback(
        async (
            activationId: number,
            workflowId?: number | null,
        ): Promise<{
            publishedVersionId: number | null;
            versions: WorkflowVersionSummary[];
        }> => {
            const response = await api.get(
                workflowId
                    ? `/activations/${activationId}/workflows/${workflowId}/versions`
                    : `/activations/${activationId}/workflow/versions`,
            );
            return {
                publishedVersionId: response.data.publishedVersionId ?? null,
                versions: response.data.versions ?? [],
            };
        },
        [api],
    );

    const restoreVersion = useCallback(
        async (
            activationId: number,
            versionId: number,
            workflowId?: number | null,
        ): Promise<WorkflowDefinition> => {
            const response = await api.post(
                workflowId
                    ? `/activations/${activationId}/workflows/${workflowId}/versions/${versionId}/restore`
                    : `/activations/${activationId}/workflow/versions/${versionId}/restore`,
                {},
            );
            return (
                response.data.definition ?? { nodes: [], edges: [] }
            );
        },
        [api],
    );

    const listRuns = useCallback(
        async (
            activationId: number,
            options: { status?: WorkflowRunStatus; limit?: number; offset?: number } = {},
        ): Promise<{ total: number; runs: WorkflowRunSummary[] }> => {
            const params = new URLSearchParams();
            if (options.status) params.set('status', options.status);
            if (options.limit != null) params.set('limit', String(options.limit));
            if (options.offset != null)
                params.set('offset', String(options.offset));
            const response = await api.get(
                `/activations/${activationId}/workflow/runs?${params.toString()}`,
            );
            return {
                total: response.data.total ?? 0,
                runs: response.data.runs ?? [],
            };
        },
        [api],
    );

    const getRunDetail = useCallback(
        async (
            activationId: number,
            runId: string,
        ): Promise<WorkflowRunDetail> => {
            const response = await api.get(
                `/activations/${activationId}/workflow/runs/${runId}`,
            );
            return response.data as WorkflowRunDetail;
        },
        [api],
    );

    return {
        getWorkflow,
        saveWorkflow,
        listWorkflows,
        createWorkflow,
        deleteWorkflow,
        importWorkflow,
        testRun,
        getCatalog,
        publishWorkflow,
        listVersions,
        restoreVersion,
        listRuns,
        getRunDetail,
    };
}
